import moment from 'moment';
import { MOMENT_TYPES } from 'utils/constants';
import { getMonthEndDate, getQuarterEndDate } from 'utils/helpers';
import {
  getTimelineDisplayDates,
  isIsoDateOnlyBeforeToday,
  isSpecialDateStatus,
  type TimelineDisplayDates
} from 'utils/timelineDisplayDates';
import {
  isEventLane,
  isItemClosed,
  isKeyDateLane
} from './monthlyItemIconUtils';
import type { MonthlyTimelineItem } from './types';

export type MonthlyBadgeStyle = 'filled' | 'outline';

/** Moment type for date resolution — mirrors TimelineCard `projectType` (never the swimlane label). */
export function resolveMonthlyProjectType(item: MonthlyTimelineItem): string {
  return item.projectType || item.type || '';
}

export function getMonthlyTimelineDisplayDates(
  item: MonthlyTimelineItem
): TimelineDisplayDates {
  return getTimelineDisplayDates({
    dates: item.dates,
    projectType: resolveMonthlyProjectType(item),
    showOnTimeline: Boolean(item.showOnTimeline),
    pco: item.pco
  });
}

/**
 * Whether the resolved display date is in the past.
 * Mirrors TimelineCard `isOverdue` date priority (end → start → endMonth → quarter → month).
 */
export function isTimelineDisplayDatePast(
  displayDates: TimelineDisplayDates
): boolean {
  if (
    isSpecialDateStatus(displayDates.dateStatus) &&
    !displayDates.start &&
    !displayDates.end &&
    !displayDates.month &&
    !displayDates.quarter &&
    !displayDates.endMonth
  ) {
    return false;
  }

  if (displayDates.end) {
    return isIsoDateOnlyBeforeToday(displayDates.end);
  }

  if (displayDates.start) {
    return isIsoDateOnlyBeforeToday(displayDates.start);
  }

  if (displayDates.endMonth) {
    const monthWithYear = displayDates.endMonth.match(/(\w+)\s+(\d{4})/);
    if (monthWithYear) {
      const endMonthDate = getMonthEndDate(
        monthWithYear[1],
        parseInt(monthWithYear[2], 10)
      );
      if (endMonthDate) {
        return moment(endMonthDate).isBefore(moment(), 'day');
      }
    } else if (displayDates.quarter) {
      const quarterMatch = displayDates.quarter.match(/Q\d\s+(\d{4})/);
      if (quarterMatch) {
        const endMonthDate = getMonthEndDate(
          displayDates.endMonth,
          parseInt(quarterMatch[1], 10)
        );
        if (endMonthDate) {
          return moment(endMonthDate).isBefore(moment(), 'day');
        }
      }
    }
  }

  if (displayDates.quarter) {
    const quarterEndDate = getQuarterEndDate(displayDates.quarter);
    if (quarterEndDate) {
      return moment(quarterEndDate).isBefore(moment(), 'day');
    }
  }

  if (displayDates.month) {
    const monthWithYear = displayDates.month.match(/(\w+)\s+(\d{4})/);
    if (monthWithYear) {
      const monthEndDate = getMonthEndDate(
        monthWithYear[1],
        parseInt(monthWithYear[2], 10)
      );
      if (monthEndDate) {
        return moment(monthEndDate).isBefore(moment(), 'day');
      }
    } else if (displayDates.quarter) {
      const quarterMatch = displayDates.quarter.match(/Q\d\s+(\d{4})/);
      if (quarterMatch) {
        const monthEndDate = getMonthEndDate(
          displayDates.month,
          parseInt(quarterMatch[1], 10)
        );
        if (monthEndDate) {
          return moment(monthEndDate).isBefore(moment(), 'day');
        }
      }
    }
  }

  return false;
}

/** Mirrors TimelineCard `shouldApplyMmprStyling` for monthly filled badges. */
export function shouldApplyMonthlyFilledBadge(
  item: MonthlyTimelineItem
): boolean {
  if (
    item.projectType === MOMENT_TYPES.PRODUCT_RELEASE ||
    item.projectType === 'Product Release without Marketing'
  ) {
    return true;
  }

  if (item.showOnTimeline) {
    return true;
  }

  const releasePhases = item.releasePhases;
  if (!item.projectType && releasePhases && Array.isArray(releasePhases)) {
    return releasePhases.some(phase => {
      const momentType = phase.generalInfo?.momentType;
      return (
        momentType === MOMENT_TYPES.PRODUCT_RELEASE ||
        momentType === 'Product Release without Marketing'
      );
    });
  }

  return false;
}

/**
 * Past Events use row opacity (quarterly compact cards), not red overdue badges.
 * Uses the same display-date past check as TimelineCard `isOverdue`.
 */
export function isMonthlyEventPast(item: MonthlyTimelineItem): boolean {
  if (isItemClosed(item)) {
    return false;
  }

  return isTimelineDisplayDatePast(getMonthlyTimelineDisplayDates(item));
}

/**
 * Red overdue badge when quarterly chip would be overdue.
 * Key Dates never use overdue chip styling in monthly.
 */
export function isMonthlyItemOverdue(
  item: MonthlyTimelineItem,
  laneLabel: string
): boolean {
  if (isKeyDateLane(laneLabel)) {
    return false;
  }

  if (isItemClosed(item)) {
    return false;
  }

  return isTimelineDisplayDatePast(getMonthlyTimelineDisplayDates(item));
}

function getMonthlyBadgeStyle(
  item: MonthlyTimelineItem,
  laneLabel: string
): MonthlyBadgeStyle {
  if (isEventLane(laneLabel) || isKeyDateLane(laneLabel)) {
    return 'outline';
  }

  if (shouldApplyMonthlyFilledBadge(item)) {
    return 'filled';
  }

  return 'outline';
}

export interface MonthlyBadgeVariantState {
  style: MonthlyBadgeStyle;
  isOverdue: boolean;
  isCompleted: boolean;
}

/** Resolves badge style flags for MonthlyDateBadge and MonthlyInlineItem. */
export function getMonthlyBadgeVariant(
  item: MonthlyTimelineItem,
  laneLabel: string
): MonthlyBadgeVariantState {
  const isCompleted = isItemClosed(item);
  const style = getMonthlyBadgeStyle(item, laneLabel);
  const isOverdue = isMonthlyItemOverdue(item, laneLabel);

  return {
    style,
    isOverdue,
    isCompleted
  };
}
